<template>
	<div class="profile-page">
		<div class="profile-page__inner _cnt">
			<div class="profile-page__top top">
				<div class="top__info">
					<div class="top__avatar _ibg-contain">
						<img src="@/assets/images/icons/account.svg" />
					</div>
					<div class="top__about">
						<div class="top__name">
							<span>{{ user.name }} </span>
							<span>{{ user.surname }}</span>
						</div>
						<div class="top__email">{{ user.email }}</div>
					</div>
				</div>
				<div class="top__balance balance">
					<div class="balance__icon _ibg-contain">
						<img src="@/assets/images/icons/wallet.svg" />
					</div>
					<div class="balance__info">
						<div class="balance__text">{{ $t('Balance') }}:</div>
						<div class="balance__price">
							<div class="balance__number _md">
								{{ userBalance }} {{ currencySymbol }}
							</div>
							<!--<div class="balance__coin _ibg-contain">
								<img src="@/assets/images/icons/coin.svg" />
							</div>-->
						</div>
					</div>
					<router-link
						to="/profile/account-balance"
						class="balance__button _button-white"
						:class="{
							balance__button_active:
								$route.path === '/profile/account-balance',
						}"
					>
						{{ $t('Top up') }}
					</router-link>
				</div>
				<BaseButton class="top__logout _button-grey" @click="logout">
					{{ $t('Log out') }}
				</BaseButton>
			</div>
			<div class="profile-page__body">
				<div class="profile-page__tabs">
					<BaseButton
						v-for="tab in tabs"
						:key="tab.key"
						:class="[
							'profile-page__tab _btn-primary _md',
							{ active: activeTab === tab.key },
						]"
						@click="changeTab(tab.key)"
						:theme="ButtonThemes.TAB"
					>
						{{ tab.label }}
					</BaseButton>
				</div>
				<component
					:is="currentTabComponent"
					:user="user"
					:orderHistory="orderHistory"
					:balance="balance"
					:countryOptions="countryOptions"
					:minTopupAmount="minTopupAmount"
					@updatePersonalInfo="updatePersonalInfo"
					@updateBillingInfo="updateBillingInfo"
					@updateProfile="updateProfile"
					@updatePassword="updatePassword"
					@openWithdraw="openWithdraw"
					@logout="logout"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import BaseButton, { ButtonThemes } from '@/components/base/BaseButton.vue';
import { mapGetters } from 'vuex';
import AccountBalance from './tabs/AccountBalance.vue';
import ChangePassword from './tabs/ChangePassword.vue';
import OrderHistory from './tabs/OrderHistory.vue';
import PaymentHistory from './tabs/PaymentHistory.vue';
import PersonalInformation from './tabs/PersonalInformation.vue';

export default {
	name: 'ProfilePage',

	components: {
		PaymentHistory,
		OrderHistory,
		ChangePassword,
		PersonalInformation,
		BaseButton,
		AccountBalance,
	},

	props: {
		isAuth: {
			type: Boolean,
			required: true,
		},
		orderHistory: {
			type: Array,
			required: true,
		},
		minTopupAmount: {
			type: Number,
			required: true,
		},
	},

	data() {
		return {
			ButtonThemes,
			tabs: [
				{
					key: 'profile',
					label: this.$t('Profile'),
				},
				{
					key: 'account-balance',
					label: this.$t('Account balance'),
				},
				{ key: 'payment-history', label: this.$t('Payment history') },
				{ key: 'order-history', label: this.$t('Order history') },
			],
			activeTab: 'profile',
			user: {
				email: '',
				name: '',
				surname: '',
				phone: '',
				phoneCountryCode: null,
				address: '',
				country: '',
				city: '',
				zip: '',
				balance: 0,
				steamId: '',
				steamProfileUrl: null,
				avatarMedium: '',
				status: '',
			},
			countryOptions: [],
		};
	},
	computed: {
		...mapGetters('user', ['userBalance']),
		...mapGetters('app', ['currencyValue']),
		...mapGetters('app', ['currencySymbol']),
		currentTabComponent() {
			switch (this.activeTab) {
				case 'account-balance':
					return 'AccountBalance';
				case 'order-history':
					return 'OrderHistory';
				case 'payment-history':
					return 'PaymentHistory';
				case 'profile':
					return 'PersonalInformation';
				default:
					return 'PersonalInformation';
			}
		},
	},
	mounted() {
		if (!this.isAuth) {
			this.$router.push('/');
		}
	},
	watch: {
		'$route.params.page'(newPage) {
			if (newPage && this.tabs.some((tab) => tab.key === newPage)) {
				this.activeTab = newPage;
			} else {
				this.activeTab = 'account-balance';
			}
		},
		isAuth(newVal) {
			if (!newVal) {
				this.$router.push('/');
			}
		},
	},
	methods: {
		openWithdraw() {
			this.$emit('openWithdraw');
		},
		changeTab(tabKey) {
			this.activeTab = tabKey;
			this.$router.push({ name: 'ProfilePage', params: { page: tabKey } });
		},

		logout() {
			this.$emit('logout');
		},

		async fetchUserData() {
			try {
				const response = await this.$http.get(
					process.env.VUE_APP_API + 'user/profile'
				);

				if (response.status === 200 && response.data) {
					const isPlaceholder = (value, placeholder) =>
						String(value ?? '').trim() === placeholder;

					this.user.name = isPlaceholder(response.data.name, 'Name')
						? ''
						: response.data.name;

					this.user.surname = isPlaceholder(
						response.data.surname,
						'Surname'
					)
						? ''
						: response.data.surname;

					this.user.email = response.data.email;
					this.user.phone = response.data.phone;
					this.user.country = response.data.country;
					this.user.city = response.data.city;
					this.user.zip = response.data.zip;
					this.user.address = response.data.address;
					this.user.balance = response.data.balance;
					this.user.steamId = response.data.steamId;
					this.user.steamProfileUrl = response.data.steam_profile_url;
					this.user.avatarMedium = response.data.avatar_medium;
					this.user.status = response.data.status;
				}
			} catch (error) {
				console.error('Error fetching user data:', error);
			}
		},

		async updatePersonalInfo(data) {
			try {
				await this.$http.put(
					process.env.VUE_APP_API + 'user/profile',
					data
				);
				this.personalInfo = data;
			} catch (error) {
				console.error('Error updating personal information:', error);
			}
		},

		async updateBillingInfo(data) {
			try {
				await this.$http.put(
					process.env.VUE_APP_API + 'user/profile',
					data
				);
				this.billingInfo = data;
			} catch (error) {
				console.error('Error updating billing information:', error);
			}
		},

		async updateProfile(data) {
			try {
				await this.$http.put(
					process.env.VUE_APP_API + 'user/profile',
					data
				);
				this.personalInfo = {
					firstName: data.name,
					lastName: data.surname,
					email: data.email,
					phone: data.phone,
				};
				this.billingInfo = {
					country: data.country,
					city: data.city,
					zipCode: data.zip,
					address: data.address,
				};
			} catch (error) {
				console.error('Error updating profile:', error);
			}
		},

		async updatePassword(passwordData) {
			try {
				const data = {
					oldPassword: passwordData.currentPassword,
					newPassword: passwordData.newPassword,
					confirmPassword: passwordData.confirmPassword,
				};
				await this.$http.post(
					process.env.VUE_APP_API + 'user/change-password',
					data
				);

				this.$message({
					message: 'Password updated successfully!',
					type: 'success',
				});
			} catch (error) {
				console.error('Error updating password:', error);

				this.$message({
					message: 'Error updating password. Please try again.',
					type: 'error',
				});
			}
		},

		async fetchCountryOptions() {
			try {
				const response = await this.$http.get(
					process.env.VUE_APP_API + 'countries'
				);
				this.countryOptions = response.data.payload;
			} catch (error) {
				console.error('Error fetching country options:', error);
			}
		},
	},

	created() {
		const page = this.$route.params.page;
		if (page && this.tabs.some((tab) => tab.key === page)) {
			this.activeTab = page;
		}

		this.fetchUserData();
		this.fetchCountryOptions();
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.profile-page {
	@include header-indent;
	@include adaptiveValue('padding-top', 60, 25);
	@include adaptiveValue('padding-bottom', 60, 25);
	position: relative;
	min-height: calc(100vh - 98px);
	@media (max-width: $md1) {
		min-height: calc(100vh - 70px);
	}
	@media (max-width: $md4) {
		min-height: calc(100vh - 50px);
	}
	&::before {
		content: '';
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		background-image: url('@/assets/images/bg/vertical-bg.png');
		background-repeat: no-repeat;
		background-size: contain;
		background-size: 100% 100%;
		z-index: -1;
	}
	&__inner {
	}
	&__top {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 80, 25);
		}
	}
	&__body {
	}
	&__tabs {
		display: flex;
		gap: 20px;
		min-height: 56px;
		position: relative;
		padding: 5px 7px 6px 7px;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 45, 20);
		}
		@media (min-width: $md2) {
			&::before {
				content: '';
				position: absolute;
				width: 100%;
				height: 100%;
				top: 0;
				left: 0;
				background-image: url('@/assets/images/bg/profile/profile-tab-bg.png');
				background-repeat: no-repeat;
				background-size: contain;
				background-size: 100% 100%;
				z-index: -1;
				border-radius: 3px;
				overflow: hidden;
			}
		}
		@media (max-width: $md2) {
			padding: 5px;
			margin-left: -10px;
			margin-right: -10px;
			background-color: var(--primary-color);
			flex-wrap: wrap;
			gap: 10px;
		}
		@media (max-width: $md4) {
			gap: 5px;
		}
	}
	&__tab {
		border-radius: 3px;
		flex: 0 1 25%;
		color: var(--primary-inverted-color);
		font-family: var(--font-family-inter);
		@include adaptiveValue('font-size', 20, 16);
		line-height: 120%;
		font-weight: 700;
		position: relative;
		z-index: 1;
		&::before {
			background-image: none;
			border-radius: 3px;
			overflow: hidden;
		}
		@media (any-hover: hover) {
			&:hover {
				&::before {
					background-image: url('@/assets/images/bg/profile/profile-tab-active.png');
				}
			}
		}
		@media (max-width: $md2) {
			flex: 0 1 calc(50% - 5px);
		}
		@media (max-width: $md5) {
			flex: 1 1 100%;
		}
		&.active {
			pointer-events: none;
			color: var(--primary-color);
			&::before {
				background-image: url('@/assets/images/bg/profile/profile-tab-active.png');
			}
		}
	}
}

.top {
	display: flex;
	gap: 20px;
	@media (max-width: $md3) {
		gap: 15px;
		flex-wrap: wrap;
	}
	&__info {
		flex: 0 1 33.333%;
		display: flex;
		@include adaptiveValue('gap', 20, 15);
		@media (max-width: $md3) {
			flex: 1 1 100%;
		}
	}
	&__avatar {
		@include adaptiveValue('min-width', 70, 40);
		@include adaptiveValue('height', 70, 40);
	}
	&__about {
		align-self: center;
	}
	&__name {
		display: flex;
		gap: 6px;
		span {
			@include adaptiveValue('font-size', 20, 16);
			color: var(--primary-color);
			line-height: 120%;
			font-weight: 500;
			text-transform: capitalize;
			display: inline-block;
		}
	}
	&__email {
		font-size: 16px;
		line-height: 120%;
		color: var(--secondary-color);
		font-weight: 500;
	}
	&__balance {
		flex: 0 1 33.333%;
		@media (max-width: $md3) {
			flex: 1 1 calc(100% - 137px);
		}
		@media (max-width: $md5) {
			flex: 1 1 100%;
		}
	}
	&__logout {
		min-width: 122px;
		margin-left: auto;
		height: fit-content;
		@media (max-width: $md5) {
			max-width: 224px;
			width: 100%;
			margin: 0 auto;
		}
	}
}

.balance {
	display: flex;
	gap: 15px;
	@media (max-width: $md5) {
		flex-wrap: wrap;
	}
	&__icon {
		align-self: center;
		min-width: 42px;
		height: 42px;
	}
	&__info {
	}
	&__text {
		font-size: 12px;
		line-height: 120%;
		color: var(--secondary-color);
		&:not(:last-child) {
			margin-bottom: 8px;
		}
	}
	&__price {
		display: flex;
		align-items: center;
		@include adaptiveValue('gap', 8, 6);
	}
	&__number {
		white-space: nowrap;
		line-height: 120%;
		font-family: var(--font-family-inter);
		font-weight: 700;
	}
	&__coin {
		@include adaptiveValue('min-width', 21, 15);
		@include adaptiveValue('height', 21, 15);
	}
	&__button {
		white-space: nowrap;
		align-self: center;
		min-height: 35px;
		min-width: 145px;
		pointer-events: auto;
		@media (max-width: $md5) {
			margin: 0 auto;
		}
		&_active {
			pointer-events: none;
		}
	}
}
</style>
